import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { GoogleButton } from "@/pages/Login";
import { AuthShell, AuthField, AuthErrorBanner, OrDivider, AuthSubmitButton } from "@/components/AuthLayout";

const ConsentCheckbox = ({ agreed, onChange }) => (
    <label
        className="flex items-start gap-3 cursor-pointer select-none"
        data-testid="register-legal-note"
    >
        <input
            type="checkbox"
            checked={agreed}
            onChange={onChange}
            className="mt-0.5 w-4 h-4 accent-[#FF4500] cursor-pointer shrink-0"
            data-testid="register-terms-checkbox"
        />
        <span className="font-mono text-[10px] text-white/50 leading-relaxed">
            I agree to the{" "}
            <Link to="/terms" target="_blank" className="text-blaze hover:underline">Terms of Use</Link> and{" "}
            <Link to="/privacy" target="_blank" className="text-blaze hover:underline">Privacy Policy</Link>, and I am
            solely responsible for how I use SPCLIVE.
        </span>
    </label>
);

const RegisterFields = ({ fields }) => (
    <>
        <AuthField
            label="Display Name"
            required
            maxLength={60}
            value={fields.displayName}
            onChange={fields.onDisplayName}
            testId="register-name-input"
        />
        <AuthField
            label="Email"
            type="email"
            required
            value={fields.email}
            onChange={fields.onEmail}
            testId="register-email-input"
        />
        <AuthField
            label="Password"
            type="password"
            required
            minLength={6}
            value={fields.password}
            onChange={fields.onPassword}
            testId="register-password-input"
        />
        <AuthField
            label="Confirm Password"
            type="password"
            required
            value={fields.confirm}
            onChange={fields.onConfirm}
            testId="register-confirm-input"
        />
    </>
);

function useRegisterForm() {
    const { user, register } = useAuth();
    const navigate = useNavigate();
    const [displayName, setDisplayName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [agreed, setAgreed] = useState(false);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (user && typeof user === "object") navigate("/console", { replace: true });
    }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (password !== confirm) {
            setError("Passwords do not match");
            return;
        }
        if (!agreed) {
            setError("Please tick the box to agree to the Terms of Use and Privacy Policy");
            return;
        }
        setBusy(true);
        try {
            await register(email, password, displayName, true);
            navigate("/console");
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    return {
        agreed,
        onAgreed: (e) => setAgreed(e.target.checked),
        error,
        busy,
        submit,
        fields: {
            displayName,
            onDisplayName: (e) => setDisplayName(e.target.value),
            email,
            onEmail: (e) => setEmail(e.target.value),
            password,
            onPassword: (e) => setPassword(e.target.value),
            confirm,
            onConfirm: (e) => setConfirm(e.target.value),
        },
    };
}

export default function Register() {
    const form = useRegisterForm();

    return (
        <AuthShell
            pageTestId="register-page"
            title="Create Account"
            subtitle="Start transforming your stream"
            footer={
                <>
                    Already registered?{" "}
                    <Link to="/login" className="text-blaze hover:underline" data-testid="goto-login-link">
                        Log in
                    </Link>
                </>
            }
        >
            <GoogleButton label="Sign up with Google" />
            <OrDivider />
            <form onSubmit={form.submit} className="space-y-4">
                <RegisterFields fields={form.fields} />
                <ConsentCheckbox agreed={form.agreed} onChange={form.onAgreed} />
                <AuthErrorBanner error={form.error} testId="register-error" />
                <AuthSubmitButton busy={form.busy} testId="register-submit-button">
                    Create Account →
                </AuthSubmitButton>
            </form>
        </AuthShell>
    );
}
